<script setup>
import {computed, onMounted, ref, watch} from 'vue'
import helpers from '@/lib/helpers'
import _ from 'lodash'

// Deliberately not the full lookup component - this list only ever holds email
// addresses, so a search box, an add button and a plain list is the whole job.

const PAGE_LIMIT = 50

const rows = ref([])
const query = ref('')
const showReleased = ref(false)

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

const typed = computed(() => query.value.trim().toLowerCase())
const isEmail = computed(() => EMAIL_RE.test(typed.value))
const alreadyListed = computed(() => rows.value.some(r => r.email === typed.value && r.active))
// only offer Add for something that is an address and isn't already suppressed
const canAdd = computed(() => isEmail.value && !alreadyListed.value)

// the search is a debounced background refresh, so it stays off the global
// spinner - the overlay sits over the search box itself and would block typing
const load = async () => {
  const params = {limit: PAGE_LIMIT}
  if (typed.value) params.q = typed.value
  if (showReleased.value) params.include_released = 1

  const res = await helpers.asyncAction('/api/dnc', 'GET', {noload: true, params})
  rows.value = Array.isArray(res) ? res : (res.data || [])
}

// the list is capped, so say so rather than quietly truncating
const capped = computed(() => rows.value.length >= PAGE_LIMIT)
const countLabel = computed(() => {
  if (!rows.value.length) return ''
  if (!capped.value) return `${rows.value.length} address${rows.value.length === 1 ? '' : 'es'}`
  return typed.value ? `first ${PAGE_LIMIT} matches` : `${PAGE_LIMIT} most recent`
})

const debouncedLoad = _.debounce(load, 250)
watch(query, debouncedLoad)
watch(showReleased, load)
onMounted(load)

const add = async () => {
  if (!canAdd.value) return

  const added = typed.value
  await helpers.asyncAction('/api/dnc', 'POST', {data: {email: added}})
  helpers.toast(added + ' will no longer be contacted')
  query.value = ''
  await load()
}

const release = async (row) => {
  if (!await helpers.confirm(`Allow contact to ${row.email} again?`))
    return

  await helpers.asyncAction(row._links.delete_url.href, row._links.delete_url.method)
  helpers.toast(row.email + ' released')
  load()
}

// source and date aren't worth a column each, but they answer "why is this
// here?" - so they ride along as the row's tooltip
const why = (row) => {
  const when = row.created ? new Date(row.created).toLocaleDateString() : ''
  const by = row.createdby_user ? ` by ${row.createdby_user}` : ''
  const src = {
    manual: 'Added by hand',
    unsubscribe: 'Unsubscribed themselves',
    bounce: 'Hard bounced',
    import: 'Migrated from the old per-record flag',
  }[row.source] || row.source

  return `${src}${by}${when ? ' on ' + when : ''}${row.reason ? ' — ' + row.reason : ''}`
}
</script>

<template>
  <modal large height="560px" title="Do Not Contact">
    <template #buttons>
      <div class="text-white-50 small align-self-center mr-2">{{ countLabel }}</div>
    </template>

    <!-- search and add stay pinned; only the list scrolls -->
    <template #top>
      <p class="text-muted small mb-2">
        Email addresses here are suppressed everywhere — they drop out of every campaign audience,
        and any lead, customer or vendor contact using one shows as Do Not Contact.
      </p>

      <div class="d-flex mb-2" style="gap:.5rem">
        <b-form-input
            v-model="query"
            placeholder="Search, or type an address to add…"
            autocomplete="off"
            @keydown.enter.prevent="add"/>
        <b-btn variant="outline-primary" class="px-4 text-nowrap"
               :disabled="!canAdd" @click="add">
          Add
        </b-btn>
      </div>

      <div class="d-flex align-items-center">
        <b-form-checkbox v-model="showReleased" switch class="small text-muted">
          Show released
        </b-form-checkbox>
        <span v-if="alreadyListed" class="small text-muted ml-auto">Already on the list</span>
      </div>
    </template>

    <div>
      <div>
        <div v-if="!rows.length" class="text-muted small text-center py-4 border rounded">
          <template v-if="typed">Nothing matches “{{ query.trim() }}”.</template>
          <template v-else>No addresses are suppressed.</template>
        </div>

        <b-list-group v-else>
          <b-list-group-item
              v-for="row in rows" :key="row.id"
              class="d-flex align-items-center py-2"
              :title="why(row)">
            <span class="text-monospace" :class="{'text-muted': !row.active}">{{ row.email }}</span>
            <b-badge v-if="!row.active" variant="light" class="ml-2">released</b-badge>

            <b-button v-if="row.active && can('dnc.write')"
                      size="sm" variant="link" class="ml-auto p-0 text-danger"
                      title="Allow contact again"
                      @click="release(row)">
              Release
            </b-button>
          </b-list-group-item>
        </b-list-group>

        <div v-if="capped" class="text-muted small text-center mt-2">
          <template v-if="typed">More addresses match — narrow the search to see them.</template>
          <template v-else>Showing the {{ PAGE_LIMIT }} most recent. Search to find any address.</template>
        </div>
      </div>
    </div>
  </modal>
</template>
